import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MaterialUICore,
  Select,
  Autocomplete,
  TextField,
} from '@iclinic/design-system';

import {
  ExecutantProfessional,
  ExecutantTypeOptions,
  LotConfig,
} from 'features/tissInvoicing/types';
import { actions } from 'features/tissInvoicing/state/lot';
import {
  getHiredOptions,
  getLotDataState,
  getLotViewState,
} from 'features/tissInvoicing/state/lot/selectors';
import { SpaceBetweenBox } from 'features/tissInvoicing/components/ui/Flexbox';
import * as Styles from './styles';

const { MenuItem } = MaterialUICore;

interface LotConfigOptionsProps {
  lotId: string;
  isNewLot: boolean;
}

function LotConfigOptions({ lotId, isNewLot }: LotConfigOptionsProps) {
  const classes = Styles.useStyles();
  const dispatch = useDispatch();

  const { configOptions, config } = useSelector(getLotViewState);
  const hiredOptions = useSelector(getHiredOptions);
  const {
    lot: { is_closed: isClosed, guides },
  } = useSelector(getLotDataState);

  const handleUpdateLot = (configPayload: LotConfig) => {
    dispatch(actions.setLotConfig(configPayload));

    if (!isNewLot)
      dispatch(actions.patchLotData({ id: lotId, guides, ...configPayload }));
  };

  const handleChange = ({
    target: { value, name },
  }: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const configPayload = {
      ...config,
      [name]: value,
    };

    const {
      version,
      executant_type: executantType,
      executant_clinic_id: executantClinicId,
      executant_professional_id: executantProfessionalId,
    } = config;

    if (name === 'clinic_health_insurance_id') {
      const healthInsurance = configOptions.clinic_health_insurances.find(
        (option) => option.id === Number(value),
      );

      if (!version && healthInsurance?.tiss_version)
        configPayload.version = healthInsurance?.tiss_version;

      if (
        !executantType &&
        (!executantProfessionalId || !executantClinicId) &&
        healthInsurance?.provider === ExecutantTypeOptions.Clinic
      ) {
        configPayload.executant_type = healthInsurance?.provider;
        configPayload.executant_clinic_id = String(
          healthInsurance?.clinic_provider_id,
        );
      }
    }

    handleUpdateLot(configPayload);
  };

  const getExecutantProfessionalName = () => {
    const {
      executant_type: executantType,
      executant_clinic_id: executantClinicId,
      executant_professional_id: executantProfessionalId,
    } = config;

    const professionalId =
      executantType === ExecutantTypeOptions.Clinic
        ? executantClinicId
        : executantProfessionalId;

    return (
      hiredOptions.find(
        ({ id, type }) =>
          id === Number(professionalId) && type === executantType,
      ) || null
    );
  };

  const handleExecutantChange = (option: ExecutantProfessional) => {
    const { id, type } = option || {};

    if (id && type)
      handleUpdateLot({
        ...config,
        executant_type: type,
        executant_professional_id:
          type === ExecutantTypeOptions.Clinic ? '' : String(id),
        executant_clinic_id:
          type === ExecutantTypeOptions.Professional ? '' : String(id),
      });
  };

  const disableInputs: boolean = useMemo(
    () => !isNewLot && isClosed,
    [isNewLot, isClosed],
  );

  return (
    <SpaceBetweenBox classes={{ root: classes.header }}>
      <Select
        label="Convênio"
        id="clinic_health_insurance_id"
        name="clinic_health_insurance_id"
        value={config.clinic_health_insurance_id}
        disabled={!isNewLot || disableInputs}
        onChange={handleChange}
      >
        {configOptions.clinic_health_insurances.map((healthInsurance) => (
          <MenuItem value={healthInsurance.id} key={healthInsurance.id}>
            {healthInsurance.name}
          </MenuItem>
        ))}
      </Select>

      <Autocomplete
        fullWidth
        id="executant_professional_id"
        classes={{ root: classes.autocomplete }}
        disabled={disableInputs}
        value={getExecutantProfessionalName()}
        options={hiredOptions}
        groupBy={({ label }) => label}
        onChange={(_, option) =>
          handleExecutantChange(option as ExecutantProfessional)
        }
        getOptionLabel={({ name }) => name}
        renderOption={({ name }) => name}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            label="Contratado executante"
            name="executant_professional_id"
            data-ga="executant_professional_id"
          />
        )}
      />

      <Select
        label="Versão"
        id="version"
        name="version"
        value={config.version}
        disabled={disableInputs}
        onChange={handleChange}
      >
        {configOptions.versions.map((version) => (
          <MenuItem value={version} key={version}>
            {version}
          </MenuItem>
        ))}
      </Select>
    </SpaceBetweenBox>
  );
}

export default LotConfigOptions;
